/**
 * Legal Pages Service
 *
 * API calls for legal pages (Privacy, Terms, About, Contact).
 * Public endpoints for reading content, admin endpoints for editing.
 *
 * @module services/legal-pages
 */

import { api } from './api';
import type { LegalPage, LegalPageId, ContactFormInput, ApiResponse } from '@budgira/shared';

/**
 * Get a legal page by ID (public)
 */
export async function getLegalPage(pageId: LegalPageId): Promise<LegalPage> {
  const response = await api.get<ApiResponse<LegalPage>>(`/legal/${pageId}`);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || 'Failed to load page');
  }
  return response.data.data;
}

/**
 * Check if contact form is enabled (public)
 */
export async function isContactEnabled(): Promise<boolean> {
  try {
    const response = await api.get<ApiResponse<{ enabled: boolean }>>('/legal/contact/enabled');
    return response.data.success && response.data.data?.enabled === true;
  } catch {
    return false;
  }
}

/**
 * Submit contact form (public)
 */
export async function submitContactForm(formData: ContactFormInput): Promise<{ message: string }> {
  const response = await api.post<ApiResponse<{ message: string }>>('/legal/contact', formData);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || 'Failed to send message');
  }
  return response.data.data;
}

/**
 * Submit contact form with attachment (authenticated users only)
 */
export async function submitContactFormWithAttachment(
  formData: ContactFormInput,
  attachment?: File
): Promise<{ message: string }> {
  // If no attachment, use the standard endpoint (more reliable)
  if (!attachment) {
    return submitContactForm(formData);
  }
  
  // With attachment, use multipart/form-data
  const data = new FormData();
  data.append('name', formData.name);
  data.append('email', formData.email);
  data.append('subject', formData.subject);
  data.append('message', formData.message);
  data.append('attachment', attachment);

  // Don't set Content-Type header manually - let axios/browser set it with boundary
  const response = await api.post<ApiResponse<{ message: string }>>(
    '/legal/contact/authenticated',
    data
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || 'Failed to send message');
  }
  return response.data.data;
}

/**
 * Get all legal pages (admin)
 */
export async function getAllLegalPages(): Promise<LegalPage[]> {
  const response = await api.get<ApiResponse<LegalPage[]>>('/legal/admin/all');
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || 'Failed to load pages');
  }
  return response.data.data;
}

/**
 * Update a legal page (admin)
 */
export async function updateLegalPage(pageId: LegalPageId, content: string): Promise<LegalPage> {
  const response = await api.put<ApiResponse<LegalPage>>(`/legal/admin/${pageId}`, { content });
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || 'Failed to update page');
  }
  return response.data.data;
}

export const legalPagesService = {
  getLegalPage,
  isContactEnabled,
  submitContactForm,
  submitContactFormWithAttachment,
  getAllLegalPages,
  updateLegalPage,
};
